import { Dropdown, Form } from "react-bootstrap";
import { SelectResponse } from "../../app/models/api/selectResponse";
import CustomSpinner from "../spinner/CustomSpinner";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

interface Props {
    data: SelectResponse[];
    label?: string;
    placeholder?: string;
    id?: string;
    showSpinner?: boolean;
    hideSearch?: boolean;
    defaultValue?: SelectResponse;
    isInvalid?: boolean;
    isValid?: boolean;
    isCountryIcon?: boolean;
    isDisable?: boolean;
    onSelect?: (item: SelectResponse) => void;
    onToggle?: () => void;
}

const CustomDropdown = ({
    data,
    label,
    placeholder,
    id,
    showSpinner,
    hideSearch,
    defaultValue,
    isInvalid,
    isValid,
    isCountryIcon,
    isDisable,
    onSelect,
    onToggle,
}: Props) => {
    const { t } = useTranslation();
    const [selectedValue, setSelectedValue] = useState<SelectResponse>();
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        if (defaultValue) {
            setSelectedValue(defaultValue);
        }
    }, [defaultValue]);

    const filteredData = useMemo(() => {
        return data.filter((item) => item.label.toLowerCase().includes(searchTerm.toLowerCase())) ?? [];
    }, [data, searchTerm]);

    const handleItemClick = useCallback(
        (item: SelectResponse) => {
            setSelectedValue(item);
            setSearchTerm("");
            if (onSelect) {
                onSelect(item);
            }
        },
        [onSelect]
    );

    const getCountryIcon = useCallback((selectResponse: SelectResponse) => {
        const { value, extraData } = selectResponse;
        const countryCode = extraData ? extraData : value;
        return countryCode ? `fi fi-${countryCode.toLowerCase()}` : "";
    }, []);

    return (
        <>
            {label ? <label className="custom-form-label">{label}</label> : undefined}
            <Dropdown
                id={`custom-dropdown${id ? "-" + id : ""}`}
                className="custom-dropdown"
                style={{ cursor: "pointer" }}
                onToggle={onToggle ? () => onToggle() : undefined}
            >
                <Dropdown.Toggle
                    disabled={isDisable ?? false}
                    as="button"
                    type="button"
                    className={`custom-form-control form-control d-flex align-items-center${
                        isInvalid ? " is-invalid" : ""
                    }${isValid ? " is-valid" : ""}`}
                >
                    {selectedValue && isCountryIcon ? (
                        <span className={`me-1 ${getCountryIcon(selectedValue)}`}></span>
                    ) : undefined}
                    {selectedValue?.label || placeholder}
                </Dropdown.Toggle>
                <Dropdown.Menu className="custom-dropdown-menu p-1" style={{ overflowX: "hidden" }}>
                    {hideSearch || data.length === 0 ? undefined : (
                        <Form.Control
                            type="text"
                            className="my-1 custom-form-control"
                            placeholder={t("elements.search")}
                            value={searchTerm}
                            disabled={showSpinner}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    )}
                    {showSpinner ? (
                        <div className="mt-2">
                            <CustomSpinner hideTextTitle={true} sizeRem={6} />
                        </div>
                    ) : data.length === 0 ? (
                        <Dropdown.Item>{t("elements.noData")}</Dropdown.Item>
                    ) : (
                        filteredData.map((item, index) => (
                            <Dropdown.Item
                                key={`${item.value || item.extraData || item.label}-${index}`}
                                onClick={() => handleItemClick(item)}
                            >
                                {isCountryIcon ? (
                                    <span className={`country-icon ${getCountryIcon(item)}`}></span>
                                ) : undefined}
                                {item.label}
                            </Dropdown.Item>
                        ))
                    )}
                </Dropdown.Menu>
            </Dropdown>
        </>
    );
};

export default CustomDropdown;
